import React, { useState } from 'react'
import './PaymentDetailsModal.css'

interface PaymentData {
  payment_link?: string
  qr_code?: string
  qr_code_text?: string
  barcode?: string
  gateway_charge_id?: string
  status?: string
  payment_method?: string
  amount?: number
}

interface PaymentDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  paymentData: PaymentData | null
  saleDescription?: string
}

export const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({
  isOpen,
  onClose,
  paymentData,
  saleDescription
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null)

  if (!isOpen || !paymentData) return null

  const copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(fieldName)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100)
  }

  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal-content" onClick={e => e.stopPropagation()}>
        <div className="payment-modal-header">
          <h2>Pagamento Criado com Sucesso!</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="payment-modal-body">
          {saleDescription && (
            <div className="payment-info-section">
              <h3>Descrição da Venda</h3>
              <p>{saleDescription}</p>
            </div>
          )}

          {paymentData.amount && (
            <div className="payment-info-section">
              <h3>Valor</h3>
              <p className="payment-amount">{formatCurrency(paymentData.amount)}</p>
            </div>
          )}

          {paymentData.payment_method && (
            <div className="payment-info-section">
              <h3>Método de Pagamento</h3>
              <p className="payment-method-badge">{paymentData.payment_method}</p>
            </div>
          )}

          {paymentData.status && (
            <div className="payment-info-section">
              <h3>Status</h3>
              <span className={`status-badge status-${paymentData.status.toLowerCase()}`}>
                {paymentData.status}
              </span>
            </div>
          )}

          {paymentData.payment_link && (
            <div className="payment-info-section">
              <h3>Link de Pagamento</h3>
              <div className="copy-field-container">
                <input
                  type="text"
                  value={paymentData.payment_link}
                  readOnly
                  className="copy-input"
                />
                <button
                  onClick={() => copyToClipboard(paymentData.payment_link!, 'link')}
                  className="copy-btn"
                >
                  {copiedField === 'link' ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
              <a
                href={paymentData.payment_link}
                target="_blank"
                rel="noopener noreferrer"
                className="open-link-btn"
              >
                Abrir Link de Pagamento
              </a>
            </div>
          )}

          {paymentData.qr_code && (
            <div className="payment-info-section qr-code-section">
              <h3>QR Code PIX</h3>
              <img src={paymentData.qr_code} alt="QR Code PIX" className="qr-code-image" />
            </div>
          )}

          {paymentData.qr_code_text && (
            <div className="payment-info-section">
              <h3>PIX Copia e Cola</h3>
              <div className="copy-field-container">
                <textarea
                  value={paymentData.qr_code_text}
                  readOnly
                  className="copy-textarea"
                  rows={3}
                />
                <button
                  onClick={() => copyToClipboard(paymentData.qr_code_text!, 'pix')}
                  className="copy-btn"
                >
                  {copiedField === 'pix' ? 'Copiado!' : 'Copiar PIX'}
                </button>
              </div>
            </div>
          )}

          {paymentData.barcode && (
            <div className="payment-info-section">
              <h3>Código de Barras (Boleto)</h3>
              <div className="copy-field-container">
                <input
                  type="text"
                  value={paymentData.barcode}
                  readOnly
                  className="copy-input"
                />
                <button
                  onClick={() => copyToClipboard(paymentData.barcode!, 'barcode')}
                  className="copy-btn"
                >
                  {copiedField === 'barcode' ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
            </div>
          )}

          {paymentData.gateway_charge_id && (
            <div className="payment-info-section">
              <h3>ID da Cobrança</h3>
              <p className="charge-id">{paymentData.gateway_charge_id}</p>
            </div>
          )}
        </div>

        <div className="payment-modal-footer">
          <button className="btn-primary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
